import React, { useState, useCallback, useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import editLectureService from "../../services/editLectureService";
import { usePermissions } from "../../hooks/usePermissions";
import {
  EDIT_LECTURE_OVERRIDE,
  EDIT_LECTURE_OWN_OVERRIDE,
} from "../../utils/editLecturePermissions";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { displayDate, toApiDate } from "../../utils/dateUtils";

const DEFAULT_PAGE_SIZE = 20;
const MAX_DATE_RANGE_DAYS = 90;

const EditOverrideHistory = () => {
  const { user } = useSelector((state) => state.auth);
  const { hasVisiblePermission } = usePermissions();
  const queryClient = useQueryClient();

  const canDeleteAny = hasVisiblePermission(EDIT_LECTURE_OVERRIDE);
  const canDeleteOwn = hasVisiblePermission(EDIT_LECTURE_OWN_OVERRIDE);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter state
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [classFilter, setClassFilter] = useState(null);
  const [originalTeacherFilter, setOriginalTeacherFilter] = useState(null);
  const [overrideTeacherFilter, setOverrideTeacherFilter] = useState(null);
  const [dateRangeError, setDateRangeError] = useState("");

  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, override: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Build query params from filters
  const queryParams = useMemo(() => {
    const params = {
      page: paginationModel.page + 1, // API is 1-indexed
      pageSize: paginationModel.pageSize,
    };
    if (fromDate) params.fromDate = toApiDate(fromDate);
    if (toDate) params.toDate = toApiDate(toDate);
    if (classFilter) params.classId = classFilter.id;
    if (originalTeacherFilter) params.originalTeacherId = originalTeacherFilter.id;
    if (overrideTeacherFilter) params.overrideTeacherId = overrideTeacherFilter.id;
    return params;
  }, [paginationModel, fromDate, toDate, classFilter, originalTeacherFilter, overrideTeacherFilter]);

  // Fetch override history
  const {
    data: historyResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["edit-override-history", queryParams],
    queryFn: async () => {
      const res = await editLectureService.getEditOverrideHistory(queryParams);
      return res.data;
    },
    keepPreviousData: true,
  });

  const records = historyResponse?.data?.records || [];
  const pagination = historyResponse?.data?.pagination || { total: 0, page: 1, pageSize: DEFAULT_PAGE_SIZE, totalPages: 0 };

  // Fetch department classes for filter dropdown
  const { data: deptScheduleResponse } = useQuery({
    queryKey: ["edit-lecture-dept-classes"],
    queryFn: async () => {
      const res = await editLectureService.getDepartmentSchedule({});
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const classList = deptScheduleResponse?.data?.classList || [];
  const teacherOptions = useMemo(() => {
    // Derive unique teachers from history records for autocomplete
    const teacherMap = new Map();
    records.forEach((r) => {
      if (r.originalTeacherId && r.originalTeacherName) {
        teacherMap.set(r.originalTeacherId, { id: r.originalTeacherId, name: r.originalTeacherName });
      }
      if (r.overrideTeacherId && r.overrideTeacherName) {
        teacherMap.set(r.overrideTeacherId, { id: r.overrideTeacherId, name: r.overrideTeacherName });
      }
    });
    return Array.from(teacherMap.values());
  }, [records]);

  // Date range validation (max 90 days)
  const validateDateRange = useCallback((from, to) => {
    if (from && to) {
      const diffDays = dayjs(to).diff(dayjs(from), "day");
      if (diffDays > MAX_DATE_RANGE_DAYS) {
        setDateRangeError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
        return false;
      }
      if (diffDays < 0) {
        setDateRangeError("End date must be after start date");
        return false;
      }
    }
    setDateRangeError("");
    return true;
  }, []);

  const handleFromDateChange = (value) => {
    setFromDate(value);
    validateDateRange(value, toDate);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    validateDateRange(fromDate, value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClassFilterChange = (_, value) => {
    setClassFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOriginalTeacherChange = (_, value) => {
    setOriginalTeacherFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOverrideTeacherChange = (_, value) => {
    setOverrideTeacherFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setClassFilter(null);
    setOriginalTeacherFilter(null);
    setOverrideTeacherFilter(null);
    setDateRangeError("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Delete override
  const handleDeleteClick = (override) => {
    setDeleteDialog({ open: true, override });
  };

  const handleDeleteConfirm = async () => {
    const { override } = deleteDialog;
    if (!override) return;

    setDeleteLoading(true);
    try {
      await editLectureService.deleteEditOverride(override.id);
      toast.success("Override deleted successfully");
      setDeleteDialog({ open: false, override: null });
      queryClient.invalidateQueries(["edit-override-history"]);
      queryClient.invalidateQueries(["edit-lecture-dept-classes"]);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to delete override";
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, override: null });
  };

  // Check if override can be deleted (today or future)
  const canDeleteOverride = useCallback(
    (row) => {
      const overrideDate = dayjs(row.overrideDate);
      const today = dayjs().startOf("day");
      if (overrideDate.isBefore(today)) return false;

      if (canDeleteAny) return true;
      if (canDeleteOwn && row.originalTeacherId === user?.teacherId) return true;
      return false;
    },
    [canDeleteAny, canDeleteOwn, user?.teacherId]
  );

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "overrideDate",
        headerName: "Override Date",
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) => displayDate(params.value),
        sortable: true,
      },
      {
        field: "className",
        headerName: "Class",
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: "timeRange",
        headerName: "Time",
        flex: 1,
        minWidth: 120,
        sortable: false,
        valueGetter: (params) => {
          const start = params.row.startTime || "";
          const end = params.row.endTime || "";
          if (!start && !end) return "—";
          return `${start.slice(0, 5)} – ${end.slice(0, 5)}`;
        },
      },
      {
        field: "originalTeacherName",
        headerName: "Original Teacher",
        flex: 1.2,
        minWidth: 140,
        sortable: true,
      },
      {
        field: "overrideTeacherName",
        headerName: "Override Teacher",
        flex: 1.2,
        minWidth: 140,
        sortable: true,
        valueFormatter: (params) => params.value || "—",
      },
      {
        field: "subjectName",
        headerName: "Original Subject",
        flex: 1,
        minWidth: 130,
        sortable: true,
        valueFormatter: (params) => params.value || "—",
      },
      {
        field: "overrideSubjectName",
        headerName: "Override Subject",
        flex: 1,
        minWidth: 130,
        sortable: true,
        valueFormatter: (params) => params.value || "—",
      },
      {
        field: "createdByName",
        headerName: "Created By",
        flex: 1,
        minWidth: 130,
        sortable: true,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.6,
        minWidth: 80,
        sortable: false,
        renderCell: (params) => {
          if (!canDeleteOverride(params.row)) return null;
          return (
            <Tooltip title="Delete Override">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(params.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [canDeleteOverride]
  );

  // Sort model (override date descending by default)
  const [sortModel, setSortModel] = useState([
    { field: "overrideDate", sort: "desc" },
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Edit Override History</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Filter Panel */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <FilterIcon color="action" />
            <Typography variant="subtitle1">Filters</Typography>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={handleFromDateChange}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={handleToDateChange}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={classList}
                getOptionLabel={(opt) => opt.name || ""}
                value={classFilter}
                onChange={handleClassFilterChange}
                renderInput={(params) => (
                  <TextField {...params} label="Class" />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={teacherOptions}
                getOptionLabel={(opt) => opt.name || ""}
                value={originalTeacherFilter}
                onChange={handleOriginalTeacherChange}
                renderInput={(params) => (
                  <TextField {...params} label="Original Teacher" />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={teacherOptions}
                getOptionLabel={(opt) => opt.name || ""}
                value={overrideTeacherFilter}
                onChange={handleOverrideTeacherChange}
                renderInput={(params) => (
                  <TextField {...params} label="Override Teacher" />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              />
            </Grid>
          </Grid>

          {dateRangeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dateRangeError}
            </Alert>
          )}

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error State */}
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.response?.data?.message || "Failed to load override history. Please try again."}
        </Alert>
      )}

      {/* DataGrid */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={records}
            columns={columns}
            getRowId={(row) => row.id}
            loading={isLoading}
            paginationMode="server"
            rowCount={pagination.total || 0}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            sortingMode="client"
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": { py: 1 },
              "& .MuiDataGrid-columnHeaders": { bgcolor: "grey.50" },
            }}
            localeText={{
              noRowsLabel: "No override records found for the selected criteria.",
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Override</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this override?
          </DialogContentText>
          {deleteDialog.override && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Class:</strong> {deleteDialog.override.className}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {displayDate(deleteDialog.override.overrideDate)}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong>{" "}
                {deleteDialog.override.startTime?.slice(0, 5)} –{" "}
                {deleteDialog.override.endTime?.slice(0, 5)}
              </Typography>
              <Typography variant="body2">
                <strong>Original Teacher:</strong> {deleteDialog.override.originalTeacherName}
              </Typography>
              <Typography variant="body2">
                <strong>Override Teacher:</strong>{" "}
                {deleteDialog.override.overrideTeacherName || "—"}
              </Typography>
            </Box>
          )}
          <DialogContentText sx={{ mt: 2 }}>
            This will restore the original schedule for this slot on the specified date.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditOverrideHistory;
